import { Avatar, cn } from '@affine/component';
import { openSettingModalAtom } from '@affine/core/components/atoms';
import {
  DocWikiStatus,
  PageCreateDateCell,
  PageSelectionCell,
  PageUpdatedDateCell,
} from '@affine/core/components/page-list';
import {
  selectionStateAtom,
  useAtomValue,
} from '@affine/core/components/page-list/scoped-atoms';
import { WorkbenchLink } from '@affine/core/modules/workbench';
import type { GetWikiDocByWorkspaceResponseOutputData } from '@docnboard/rest';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { listsPropsAtom } from './atoms';
import { createAvatarProps, pageMetaToListItemProp } from './helpers';
import type { PageListItemProps, PageListItemRendererProps } from './types';

const baseCellStyle = 'flex flex-[2] justify-center items-center';

export const PageListItemRenderer = ({
  item,
  disabled,
}: PageListItemRendererProps) => {
  const props = useAtomValue(listsPropsAtom);
  const { selectionActive } = useAtomValue(selectionStateAtom);

  const page = item as GetWikiDocByWorkspaceResponseOutputData;
  return (
    <PageListItem
      {...pageMetaToListItemProp(page, {
        ...props,
        selectable: !!selectionActive,
        disabled: !!disabled,
      })}
    />
  );
};

export const PageListItem = (props: PageListItemProps) => {
  const { selectedIds, disabled } = props;
  const hasSelected = (selectedIds?.length ?? 0) > 0;

  const setOpenSettingModal = useSetAtom(openSettingModalAtom);

  const handleNavigate = useCallback(() => {
    setOpenSettingModal(prev => ({ ...prev, open: false }));
  }, [setOpenSettingModal]);

  const content = (
    <div className="flex py-2 border-b border-gray-200 min-w-[700px] !text-[14px]">
      <div
        className={cn(
          baseCellStyle,
          'flex-[3] gap-2 pl-1 justify-start overflow-hidden'
        )}
      >
        <PageSelectionCell
          onSelectedChange={props.onSelectedChange}
          selectable={props.selectable}
          selected={props.selected}
          disabled={disabled}
        />
        <div className="overflow-hidden whitespace-nowrap text-ellipsis">
          {props.title}
        </div>
      </div>

      <div className={baseCellStyle}>
        <DocWikiStatus status={props.status} />
      </div>

      <div className={baseCellStyle}>
        <PageCreateDateCell createDate={props.createdAt} />
      </div>

      <div className={baseCellStyle}>
        <PageUpdatedDateCell updatedDate={props.updatedAt} />
      </div>

      <div className={cn(baseCellStyle, 'flex-1')}>
        <Avatar {...createAvatarProps(props.createdBy)} />
      </div>

      <div className={cn(baseCellStyle, 'flex-1')}>
        <Avatar {...createAvatarProps(props.updatedBy)} />
      </div>
    </div>
  );

  // Если выбран хоть 1 документ (есть активированный чекбокс),
  // то убираем возможность перехода в документ
  if (hasSelected || disabled) {
    return <div>{content}</div>;
  }

  // TODO не работает переход в документ другого воркспейса
  return (
    <WorkbenchLink to={props.to} onClick={handleNavigate}>
      {content}
    </WorkbenchLink>
  );
};
